import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, CreditCard, Trophy, CalendarDays, ChevronRight, Crown, ShieldAlert, MapPin, Vote } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, brl, fmtDate, firstName, fileUrl } from "../lib/api";
import { Loader, Avatar } from "../components/common";
import { AdCarousel, ProductCarousel } from "../components/Carousels";
import { Feed } from "../components/Feed";

const Tile = ({ to, icon: Icon, title, sub, testid, i }) => (
  <Link to={to} data-testid={testid} className={`card-x card-hover group flex flex-col items-center p-5 text-center rise rise-${i}`}>
    <Icon className="h-10 w-10 text-[#E4F222]" strokeWidth={1.5} />
    <div className="h-display mt-3 text-2xl">{title}</div>
    <div className="mt-0.5 flex items-center gap-1 text-sm text-[#94A3B8]">{sub}<ChevronRight className="h-4 w-4 text-[#E4F222] transition-transform group-hover:translate-x-1" /></div>
  </Link>
);

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const [d, setD] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
    api.get("/sponsors").then((r) => setSponsors(r.data));
  }, []);
  if (!d) return <Loader />;
  const c = d.charges;
  return (
    <div className="space-y-6">
      <div className="rise">
        <h1 data-testid="dashboard-greeting" className="h-display text-4xl sm:text-5xl">Olá, {firstName(user.name)}!</h1>
        <p className="text-sm text-[#94A3B8]">Veja o que está acontecendo no circuito hoje.</p>
      </div>

      {isAdmin && d.pending_approvals > 0 && (
        <Link to="/app/admin" data-testid="pending-approvals-alert" className="card-x flex items-center gap-3 border-[#E4F222]/50 p-4 rise">
          <ShieldAlert className="h-6 w-6 text-[#E4F222]" />
          <div className="flex-1 text-sm"><b>{d.pending_approvals}</b> cadastro(s) aguardando sua aprovação</div>
          <ChevronRight className="h-4 w-4 text-[#E4F222]" />
        </Link>
      )}

      <AdCarousel placement="home" title="Publicidade" />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile i={1} to="/app/atletas" icon={Users} title="Atletas" sub={`Total: ${d.athletes}`} testid="tile-atletas" />
        <Tile i={2} to="/app/cobrancas" icon={CreditCard} title="Cobranças" sub={isAdmin ? `Pagas: ${c.pago}` : `Abertas: ${c.pendente + c.atrasado}`} testid="tile-cobrancas" />
        <Tile i={3} to="/app/ranking" icon={Trophy} title="Ranking" sub="Top 10" testid="tile-ranking" />
        <Tile i={4} to="/app/etapas" icon={CalendarDays} title="Etapas" sub={`Total: ${d.stages}`} testid="tile-etapas" />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <div className="card-x p-5 rise rise-2">
          <div className="mb-4 text-sm font-semibold text-[#94A3B8]">{isAdmin ? "Resumo financeiro" : "Seu resumo"}</div>
          <div className="grid grid-cols-3 divide-x divide-[#232B3E] text-center">
            <div><div data-testid="summary-registrations" className="font-display text-3xl font-bold">{d.registrations}</div><div className="text-xs text-[#94A3B8]">Inscrições</div></div>
            <div><div data-testid="summary-received" className="font-display text-3xl font-bold">{brl(isAdmin ? c.received : c.open_amount)}</div><div className="text-xs text-[#94A3B8]">{isAdmin ? "Recebido" : "Em aberto"}</div></div>
            <div><div data-testid="summary-late" className="font-display text-3xl font-bold text-red-400">{c.atrasado}</div><div className="text-xs text-[#94A3B8]">Em atraso</div></div>
          </div>
        </div>
        {d.open_stage ? (
          <Link to={`/app/etapas/${d.open_stage.id}`} data-testid="open-stage-card" className="card-x card-hover relative overflow-hidden p-5 rise rise-3">
            {d.open_stage.cover_path && <img src={fileUrl(d.open_stage.cover_path)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />}
            <div className="relative">
              <div className="eyebrow text-[#E4F222]">Inscrições abertas</div>
              <div className="h-display mt-1 text-3xl">{d.open_stage.name}</div>
              <div className="mt-2 flex flex-wrap gap-3 text-xs text-[#94A3B8]">
                <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(d.open_stage.date)}</span>
                {d.open_stage.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{d.open_stage.location}</span>}
              </div>
              <div className="mt-4 inline-flex items-center gap-1 rounded-full bg-[#E4F222] px-4 py-1.5 text-sm font-bold text-black">Inscrever dupla <ChevronRight className="h-4 w-4" /></div>
            </div>
          </Link>
        ) : (
          <Link to="/app/votacao" className="card-x card-hover flex items-center gap-4 p-5" data-testid="polls-card">
            <Vote className="h-9 w-9 text-[#E4F222]" /><div><div className="h-display text-2xl">Votação</div><div className="text-sm text-[#94A3B8]">{d.open_polls} votação(ões) aberta(s)</div></div>
          </Link>
        )}
      </div>

      <Link to="/app/ranking" data-testid="top3-card" className="card-x flex items-center gap-4 border-[#E4F222] p-5 lime-glow rise rise-4">
        <Crown className="h-9 w-9 fill-[#E4F222] text-[#E4F222]" />
        <div className="flex-1">
          <div className="h-display text-2xl text-[#E4F222]">Top 3 do circuito</div>
          <div className="mt-2 flex flex-wrap gap-4">
            {d.top3.length ? d.top3.map((a) => (
              <div key={a.user_id} className="flex items-center gap-2"><Avatar user={a} size={30} /><span className="text-sm">{a.position}º {firstName(a.name)} · <b>{a.total}</b> pts</span></div>
            )) : <span className="text-sm text-[#94A3B8]">Ainda sem pontuação lançada.</span>}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-[#E4F222]" />
      </Link>

      <ProductCarousel />

      <Feed />

      {sponsors.length > 0 && (
        <div>
          <div className="eyebrow mb-3">Patrocinadores</div>
          <div className="no-scrollbar flex gap-3 overflow-x-auto">
            {sponsors.map((s) => (
              <a key={s.id} href={s.link || undefined} target="_blank" rel="noreferrer" className="card-x flex h-20 w-44 shrink-0 items-center justify-center p-2">
                {s.image_path ? <img src={fileUrl(s.image_path)} alt={s.name} className="max-h-full object-contain" /> : <span className="h-display text-lg">{s.name}</span>}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
